import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  message,
  Modal,
  Form,
  Card,
  List,
  Popconfirm,
  Spin,
  Switch,
  Select,
  Tooltip,
  InputNumber,
  Divider,
  Empty,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HeartOutlined,
  HeartFilled,
  ClockCircleOutlined,
} from "@ant-design/icons";
import drEasyApi from "../../../Api/Customapi";
import ImageUploader from "../../Ui/ImageUploader";
import { getImg } from "../../../Api/getImg";
import { MdHomeRepairService, MdOutlineMedicalServices } from "react-icons/md";
import { useClinics } from "../../../hooks/useClinics";
const role=localStorage.getItem("role")
const ServicesScreen = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [favoriteLoading, setFavoriteLoading] = useState(new Set());
  const [form] = Form.useForm();

  const [showDiscount, setShowDiscount] = useState(false);
  const [showDuration, setShowDuration] = useState(false);
  const [showPrice, setShowPrice] = useState(false);
  const { clinicsQuery } = useClinics();
  const [selectedClinic, setSelectedClinic] = useState(null);

  const clinicOptions = (clinicsQuery?.data?.clinics ?? []).map((clinic) => ({
    value: clinic.clinicId,
    label: clinic.clinicName,
  }));

  useEffect(() => {
    fetchServices();
  }, [selectedClinic]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await drEasyApi.get("/api/Services/GetMyServices", {
        params: { clinicId: selectedClinic },
      });
      setServices(res.data || []);
    } catch (err) {
      if (err?.response?.status === 403) {
        window.location.href = "/sub";
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToggles = () => {
    setShowDiscount(false);
    setShowDuration(false);
    setShowPrice(false);
  };

  const handleAdd = () => {
    setEditingService(null);
    form.resetFields();
    resetToggles();
    setIsModalVisible(true);
  };

  const handleEdit = (service) => {
    setEditingService(service);

    setShowDiscount(!!service.showDiscount);
    setShowDuration(!!service.showDuration);
    setShowPrice(!!service.showPrice);

    form.setFieldsValue({
      titleEn: service.titleEn,
      titleAr: service.titleAr,
      descriptionEn: service.descriptionEn,
      descriptionAr: service.descriptionAr,
      photoID: service.photoID,
      price: service.price ?? undefined,
      offerPercentage: service.offerPercentage ?? undefined,
      durationInMin: service.durationInMin ?? undefined,
      clinicId: service.clinicId ?? undefined,
    });

    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await drEasyApi.delete(`/api/Services/Delete`, { params: { id } });
      message.success("Service deleted");
      fetchServices();
    } catch {
      message.error("Failed to delete service");
    }
  };

  const handleToggleFavorite = async (serviceId) => {
    const service = services.find((s) => s.id === serviceId);
    const currentFavoriteCount = services.filter((s) => s.isFavorite).length;

    if (!service.isFavorite && currentFavoriteCount >= 4) {
      message.warning("You can only have a maximum of 4 favorite services");
      return;
    }

    setFavoriteLoading((prev) => new Set([...prev, serviceId]));
    try {
      await drEasyApi.put("/api/Services/toggleFavorite", { serviceId });
      setServices((prev) =>
        prev.map((s) =>
          s.id === serviceId ? { ...s, isFavorite: !s.isFavorite } : s,
        ),
      );
      message.success(
        service.isFavorite ? "Removed from favorites" : "Added to favorites",
      );
    } catch {
      message.error("Failed to update favorite status");
    } finally {
      setFavoriteLoading((prev) => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const commonPayload = {
        titleEn: values.titleEn,
        titleAr: values.titleAr,
        descriptionEn: values.descriptionEn,
        descriptionAr: values.descriptionAr,
        photoID: values.photoID,
        price: values.price ?? null,
        offerPercentage: values.offerPercentage ?? null,
        durationInMin: values.durationInMin ?? null,
        showDiscount,
        showDuration,
        showPrice,
        clinicId: values.clinicId ?? null,
      };

      if (editingService) {
        await drEasyApi.put("/api/Services/Edit", {
          id: editingService.id,
          title: values.titleEn,
          description: values.descriptionEn,
          clinicId: values.clinicId ?? null,
          ...commonPayload,
        });
        message.success("Service updated");
      } else {
        await drEasyApi.post("/api/Services/Add", commonPayload);
        message.success("Service added");
      }

      fetchServices();
      setIsModalVisible(false);
    } catch (err) {
      if (err?.response?.status === 400) {
        message.error(
          err.response.data.errors[0] || "Please check your input and try again",
        );
      } else if (err?.errorFields) {
        message.error("Please fill all required fields");
      } else {
        message.error("Failed to submit service");
      }
    }
  };

  const handleToggleActive = async (serviceId, isActive) => {
    try {
      await drEasyApi.put("/api/Services/toogleActive", {
        serviceId,
        isActive,
      });
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, isActive } : s)),
      );
      message.success(`Service ${isActive ? "activated" : "deactivated"}`);
    } catch {
      message.error("Failed to update service status");
    }
  };

  const favoriteCount = services.filter((s) => s.isFavorite).length;

  // Favorite heart button (shared between image / no-image covers)
  const renderFavoriteButton = (item, canToggleFavorite) => (
    <Button
      type="text"
      className="absolute top-2 right-2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-1 shadow-sm"
      icon={
        favoriteLoading.has(item.id) ? (
          <Spin size="small" />
        ) : item.isFavorite ? (
          <HeartFilled style={{ color: "#ff4d4f", fontSize: "18px" }} />
        ) : (
          <HeartOutlined
            style={{
              color: canToggleFavorite ? "#666" : "#ccc",
              fontSize: "18px",
            }}
          />
        )
      }
      onClick={() => handleToggleFavorite(item.id)}
      disabled={!canToggleFavorite}
      title={!canToggleFavorite ? "Maximum 4 favorites allowed" : ""}
    />
  );

  return (
    <div className="p-3 sm:p-4 md:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-3 mb-4">
        <div className="min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
            <h2 className="text-lg md:text-xl font-semibold">My Services</h2>
            {role!=7 && <Select
              className="w-full sm:w-52"
              placeholder="Select Clinic"
              value={selectedClinic}
              onChange={(val) => setSelectedClinic(val ?? null)}
              options={clinicOptions}
              allowClear
            />}
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Favorites: {favoriteCount}/4 (You can mark up to 4 services as
            favorites)
          </p>
        </div>

       {role!=7 && <Button
          type="default"
          size="large"
          className="w-full md:w-auto bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover md:hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add Service
        </Button>}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-16">
          <Spin size="large" />
        </div>
      ) : services.length === 0 ? (
        <Empty className="py-16" />
      ) : (
        <List
          grid={{ gutter: [16, 16], xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4 }}
          dataSource={services}
          renderItem={(item) => {
            const canToggleFavorite = item.isFavorite || favoriteCount < 4;
            return (
              <List.Item className="!mb-0">
                <Card
                  className="overflow-hidden shadow-md border border-[#298d9f73] hover:shadow-lg transition-all rounded-lg relative h-full [&_.ant-card-meta-title]:!whitespace-normal [&_.ant-card-meta-detail]:min-w-0"
                  cover={
                    <div className="bg-[#f6fcfd] p-2 flex justify-center items-center h-44 sm:h-60 relative">
                      {item.photoID ? (
                        <img
                          src={getImg(item.photoID)}
                          alt="Service"
                          className="max-h-full w-full object-contain rounded"
                        />
                      ) : (
                        <span className="min-h-full w-full flex justify-center items-center">
                          <MdOutlineMedicalServices className="text-center text-primary w-10 h-10 md:w-14 md:h-14" />
                        </span>
                      )}
                      {role!=7 && renderFavoriteButton(item, canToggleFavorite)}
                    </div>
                  }
                  actions={role!=7&&[
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(item)}
                      type="text"
                      key="edit"
                    />,
                    <Popconfirm
                      title="Are you sure to delete this service?"
                      onConfirm={() => handleDelete(item.id)}
                      overlayStyle={{ maxWidth: "calc(100vw - 24px)" }}
                      key="delete"
                    >
                      <Button danger icon={<DeleteOutlined />} type="text" />
                    </Popconfirm>,
                  ]}
                >
                  <Card.Meta
                    title={
                      <div className="flex justify-between items-start gap-2 text-base md:text-lg font-semibold text-[#298d9f]">
                        <p className="flex items-start gap-1 min-w-0 break-words leading-snug m-0">
                          <MdHomeRepairService className="shrink-0 mt-1" />
                          <span className="min-w-0">{item.titleEn}</span>
                        </p>
                        {role!=7&&
                        <Tooltip
                          title={item.isActive ? "Deactivate Service" : "Activate Service"}
                        >
                          <Switch
                            className="shrink-0"
                            style={{
                              backgroundColor: item.isActive ? "#46a049ff" : "#9e9595ff",
                            }}
                            checked={item.isActive}
                            onChange={(checked) =>
                              handleToggleActive(item.id, checked)
                            }
                          />
                        </Tooltip>}
                      </div>
                    }
                    description={
                      <>
                        <p
                          className="text-sm text-gray-600 min-h-[40px] break-words"
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {item.descriptionEn}
                        </p>
                        <div className="min-h-[4rem] sm:h-20">
                          {item.price != null && (
                            <p className="mt-2 text-base font-semibold text-[#298d9f] flex items-center gap-1">
                              {item.price}{" "}
                              <span className="font-bold">EGP</span>
                            </p>
                          )}
                          {item.offerPercentage != null && (
                            <p className="mt-1 text-sm font-medium text-green-600">
                              {item.offerPercentage}% Off
                            </p>
                          )}
                          {item.durationInMin != null && (
                            <p className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <ClockCircleOutlined /> {item.durationInMin} min
                            </p>
                          )}
                        </div>
                      </>
                    }
                  />
                </Card>
              </List.Item>
            );
          }}
        />
      )}

      <Modal
        title={editingService ? "Edit Service" : "Add Service"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        centered
        width="min(520px, calc(100vw - 24px))"
        styles={{ body: { maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" } }}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSubmit}
            style={{ backgroundColor: "#298d9f", borderColor: "#298d9f" }}
          >
            Save
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="titleEn"
            label="Title"
            rules={[{ required: true, message: "Please enter title" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="titleAr"
            label="Title (In Arabic)"
            rules={[{ required: true, message: "Please enter title" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="descriptionEn" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="descriptionAr" label="Description (In Arabic)">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="clinicId" label="Clinic">
            <Select placeholder="Select Clinic" allowClear options={clinicOptions} />
          </Form.Item>

          <div className="p-2 sm:p-4 rounded-xl space-y-4 mb-4">
            {/* ── Price Toggle + Field ── */}
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-700">Price</div>
              <Switch
                checked={showPrice}
                onChange={(val) => setShowPrice(val)}
                checkedChildren="On"
                unCheckedChildren="Off"
                style={{ backgroundColor: showPrice ? "#298e9f" : undefined }}
              />
            </div>
            <Form.Item name="price" className="!mb-0">
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Enter price"
                addonAfter="EGP"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E")
                    e.preventDefault();
                }}
              />
            </Form.Item>

            <Divider className="!my-2" />

            {/* ── Discount Toggle + Field ── */}
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-700">
                Discount Percentage
              </div>
              <Switch
                checked={showDiscount}
                onChange={(val) => setShowDiscount(val)}
                checkedChildren="On"
                unCheckedChildren="Off"
                style={{ backgroundColor: showDiscount ? "#298e9f" : undefined }}
              />
            </div>
            <Form.Item name="offerPercentage" className="!mb-0">
              <InputNumber
                min={1}
                max={100}
                step={1}
                precision={0}
                className="w-full"
                placeholder="e.g. 20"
                addonAfter="%"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E")
                    e.preventDefault();
                }}
              />
            </Form.Item>

            <Divider className="!my-2" />

            {/* ── Duration Toggle + Field ── */}
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-700">Time Slot</div>
              <Switch
                checked={showDuration}
                onChange={(val) => setShowDuration(val)}
                checkedChildren="On"
                unCheckedChildren="Off"
                style={{ backgroundColor: showDuration ? "#298e9f" : undefined }}
              />
            </div>
            <Form.Item name="durationInMin" className="!mb-0">
              <InputNumber
                min={1}
                step={1}
                precision={0}
                className="w-full"
                placeholder="e.g. 60"
                addonAfter="min"
                onKeyDown={(e) => {
                  if (e.key === "-" || e.key === "e" || e.key === "E")
                    e.preventDefault();
                }}
              />
            </Form.Item>
          </div>

          <Form.Item name="photoID" label="Service Image">
            <ImageUploader form={form} value={form.getFieldValue("photoID")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ServicesScreen;